import { useEffect, useRef, useState } from 'react'
import {
  MusicNote,
  Pause,
  PlayArrow,
  SkipNext,
  SkipPrevious,
  VolumeOff,
  VolumeUp,
} from '@mui/icons-material'
import { Tooltip } from '@mui/material'
import './ambient-player.style.scss'

const PLAYER_STORAGE_KEY = 'to-focus:ambient-player'
const AMBIENT_PLAYER_PLAY_EVENT = 'to-focus:ambient-player-play'

const SOUND_OPTIONS = [
  {
    id: 'lofi-study-live',
    label: 'Lo-fi Study Live',
    description: 'Base contínua para estudo',
    videoId: 'jfKfPfyJRdk',
  },
  {
    id: 'lofi-space',
    label: 'Lo-fi Space',
    description: 'Lo-fi espacial e contínuo',
    videoId: 'EPeWXMvZEkk',
  },

  {
    id: 'lofi-warm-playlist',
    label: 'Lo-fi Warm',
    description: 'Chillhop suave para manter o ritmo',
    videoId: '9xN4Dd-k8yQ',
  },
  {
    id: 'lofi-night-jazz',
    label: 'Lo-fi Night',
    description: 'Noite calma com pegada jazz',
    videoId: '-Wn_EQxXwIA',
  },
  {
    id: 'lofi-taiki',
    label: 'Lo-fi Taiki',
    description: 'Playlist leve para foco prolongado',
    videoId: '1nEXaiLUz44',
  },
  {
    id: 'lofi-guitar',
    label: 'Lo-fi Guitar',
    description: 'Mix noturno mais melódico',
    videoId: '_Ax7t_nc-ow',
  },
]

let mediaApiPromise

const getSavedPlayerState = () => {
  try {
    const rawState = localStorage.getItem(PLAYER_STORAGE_KEY)
    if (!rawState) {
      return {
        selectedOptionId: SOUND_OPTIONS[0].id,
        isMuted: false,
        shouldPlay: false,
      }
    }

    const parsedState = JSON.parse(rawState)

    return {
      selectedOptionId: parsedState?.selectedOptionId ?? SOUND_OPTIONS[0].id,
      isMuted: Boolean(parsedState?.isMuted),
      shouldPlay: Boolean(parsedState?.shouldPlay),
    }
  } catch (error) {
    return {
      selectedOptionId: SOUND_OPTIONS[0].id,
      isMuted: false,
      shouldPlay: false,
    }
  }
}

const loadMediaApi = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Media API indisponível'))
  }

  if (window.YT?.Player) {
    return Promise.resolve(window.YT)
  }

  if (mediaApiPromise) {
    return mediaApiPromise
  }

  mediaApiPromise = new Promise(resolve => {
    const previousReady = window.onYouTubeIframeAPIReady

    window.onYouTubeIframeAPIReady = () => {
      if (typeof previousReady === 'function') {
        previousReady()
      }

      resolve(window.YT)
    }

    const existingScript = document.querySelector(
      'script[src="https://www.youtube.com/iframe_api"]'
    )

    if (!existingScript) {
      const script = document.createElement('script')
      script.src = 'https://www.youtube.com/iframe_api'
      document.body.appendChild(script)
    }
  })

  return mediaApiPromise
}

const getOptionById = optionId =>
  SOUND_OPTIONS.find(({ id }) => id === optionId) ?? SOUND_OPTIONS[0]

const AmbientPlayer = () => {
  const savedPlayerState = getSavedPlayerState()
  const [selectedOptionId, setSelectedOptionId] = useState(
    savedPlayerState.selectedOptionId
  )
  const [isMuted, setIsMuted] = useState(savedPlayerState.isMuted)
  const [isPlaying, setIsPlaying] = useState(savedPlayerState.shouldPlay)
  const [isReady, setIsReady] = useState(false)
  const playerHostRef = useRef(null)
  const playerRef = useRef(null)
  const shouldPlayRef = useRef(savedPlayerState.shouldPlay)
  const currentVideoIdRef = useRef(
    getOptionById(savedPlayerState.selectedOptionId).videoId
  )

  const selectedOption = getOptionById(selectedOptionId)

  useEffect(() => {
    const persistedState = {
      selectedOptionId,
      isMuted,
      shouldPlay: isPlaying,
    }

    localStorage.setItem(PLAYER_STORAGE_KEY, JSON.stringify(persistedState))
  }, [isMuted, isPlaying, selectedOptionId])

  useEffect(() => {
    let isMounted = true

    const createPlayer = async () => {
      const mediaApi = await loadMediaApi()

      if (!isMounted || !playerHostRef.current || playerRef.current) {
        return
      }

      playerRef.current = new mediaApi.Player(playerHostRef.current, {
        videoId: selectedOption.videoId,
        playerVars: {
          autoplay: 0,
          controls: 0,
          cc_load_policy: 0,
          disablekb: 1,
          fs: 0,
          iv_load_policy: 3,
          playsinline: 1,
          rel: 0,
          origin: window.location.origin,
        },
        events: {
          onReady: event => {
            if (!isMounted) {
              return
            }

            setIsReady(true)
            currentVideoIdRef.current = selectedOption.videoId

            if (isMuted) {
              event.target.mute()
            } else {
              event.target.unMute()
            }

            if (shouldPlayRef.current) {
              event.target.playVideo()
            } else {
              event.target.pauseVideo()
            }
          },
          onStateChange: event => {
            if (!isMounted || !window.YT?.PlayerState) {
              return
            }

            if (event.data === window.YT.PlayerState.PLAYING) {
              setIsPlaying(true)
            }

            if (
              event.data === window.YT.PlayerState.PAUSED ||
              event.data === window.YT.PlayerState.ENDED
            ) {
              setIsPlaying(false)
            }
          },
        },
      })
    }

    createPlayer()

    return () => {
      isMounted = false

      if (playerRef.current?.destroy) {
        playerRef.current.destroy()
        playerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!isReady || !playerRef.current) {
      return
    }

    const nextOption = getOptionById(selectedOptionId)

    if (currentVideoIdRef.current === nextOption.videoId) {
      return
    }

    currentVideoIdRef.current = nextOption.videoId

    if (isPlaying) {
      playerRef.current.loadVideoById(nextOption.videoId)
      return
    }

    playerRef.current.cueVideoById(nextOption.videoId)
  }, [isPlaying, isReady, selectedOptionId])

  useEffect(() => {
    if (!isReady || !playerRef.current) {
      return
    }

    if (isMuted) {
      playerRef.current.mute()
      return
    }

    playerRef.current.unMute()
  }, [isMuted, isReady])

  useEffect(() => {
    const handleExternalPlay = () => {
      shouldPlayRef.current = true
      setIsPlaying(true)

      if (isReady && playerRef.current) {
        playerRef.current.playVideo()
      }
    }

    window.addEventListener(AMBIENT_PLAYER_PLAY_EVENT, handleExternalPlay)

    return () => {
      window.removeEventListener(AMBIENT_PLAYER_PLAY_EVENT, handleExternalPlay)
    }
  }, [isReady])

  const changeOption = nextOptionId => {
    setSelectedOptionId(nextOptionId)
  }

  const handleTogglePlay = () => {
    if (!isReady || !playerRef.current) {
      return
    }

    if (isPlaying) {
      shouldPlayRef.current = false
      playerRef.current.pauseVideo()
      setIsPlaying(false)
      return
    }

    shouldPlayRef.current = true
    playerRef.current.playVideo()
    setIsPlaying(true)
  }

  const handleToggleMute = () => {
    setIsMuted(currentValue => !currentValue)
  }

  const handleStepOption = direction => {
    const currentIndex = SOUND_OPTIONS.findIndex(
      ({ id }) => id === selectedOptionId
    )
    const nextIndex =
      (currentIndex + direction + SOUND_OPTIONS.length) % SOUND_OPTIONS.length

    changeOption(SOUND_OPTIONS[nextIndex].id)
  }

  return (
    <div className="ambient-player">
      <div className="ambient-player__preview" aria-hidden="true">
        <img
          className="ambient-player__preview__image"
          src={`https://i.ytimg.com/vi/${selectedOption.videoId}/mqdefault.jpg`}
          alt=""
        />
        <div ref={playerHostRef} className="ambient-player__player-host" />
        <div className="ambient-player__preview__overlay" />
      </div>

      <div className="ambient-player__meta">
        <div className="ambient-player__meta__eyebrow">
          <MusicNote fontSize="inherit" />
          <span>Trilha de foco</span>
        </div>

        <strong className="ambient-player__meta__title">
          {selectedOption.label}
        </strong>

        <span className="ambient-player__meta__description">
          {selectedOption.description}
        </span>
      </div>

      <div className="ambient-player__controls">
        <Tooltip title="Trilha anterior">
          <button
            className="ambient-player__control-button"
            type="button"
            onClick={() => handleStepOption(-1)}
          >
            <SkipPrevious />
          </button>
        </Tooltip>

        <Tooltip title={isPlaying ? 'Pausar áudio' : 'Tocar áudio'}>
          <button
            className="ambient-player__control-button ambient-player__control-button--play"
            type="button"
            onClick={handleTogglePlay}
            disabled={!isReady}
          >
            {isPlaying ? <Pause /> : <PlayArrow />}
          </button>
        </Tooltip>

        <Tooltip title="Próxima trilha">
          <button
            className="ambient-player__control-button"
            type="button"
            onClick={() => handleStepOption(1)}
          >
            <SkipNext />
          </button>
        </Tooltip>

        <Tooltip title={isMuted ? 'Ativar som' : 'Silenciar'}>
          <button
            className="ambient-player__control-button"
            type="button"
            onClick={handleToggleMute}
            disabled={!isReady}
          >
            {isMuted ? <VolumeOff /> : <VolumeUp />}
          </button>
        </Tooltip>
      </div>
    </div>
  )
}

export { AmbientPlayer, AMBIENT_PLAYER_PLAY_EVENT }
